import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api, errMsg, money } from "../lib/api";
import { useSeo } from "../lib/site";
import { CallButton } from "../components/site/Blocks";

const CAT = { labor: "Labor", materials: "Materials", disposal: "Disposal", rental: "Rental", permit: "Permit", third_party: "Third-party", other: "Other", minimum: "Minimum" };

export function TotalsTable({ totals, testid = "quote-totals" }) {
  return (
    <div className="overflow-x-auto" data-testid={testid}>
      <table className="w-full min-w-[480px] text-left text-base">
        <thead><tr className="border-b-2 border-[#171717]"><th className="py-2">Item</th><th>Type</th><th className="text-right">Amount</th><th className="text-right">Senior savings</th></tr></thead>
        <tbody>
          {totals.lines.map((l, i) => (
            <tr key={i} className="border-b border-neutral-200"><td className="py-2">{l.description}{l.qty !== 1 && <span className="text-neutral-600"> ({l.qty} × {money(l.unit_price)})</span>}</td><td>{CAT[l.category] || l.category}</td><td className="text-right">{money(l.amount)}</td><td className="text-right text-[#B81B25]">{l.discount ? `-${money(l.discount)}` : "—"}</td></tr>
          ))}
        </tbody>
      </table>
      <dl className="ml-auto mt-4 max-w-sm space-y-1 text-base">
        <div className="flex justify-between"><dt>Subtotal</dt><dd>{money(totals.subtotal)}</dd></div>
        {totals.discount_total > 0 && <div className="flex justify-between font-bold text-[#B81B25]"><dt>Senior discount (you save)</dt><dd data-testid={`${testid}-discount`}>-{money(totals.discount_total)}</dd></div>}
        <div className="flex justify-between"><dt>Tax</dt><dd>{money(totals.tax)}</dd></div>
        <div className="flex justify-between border-t-2 border-[#171717] pt-2 text-xl font-bold"><dt>Total</dt><dd data-testid={`${testid}-total`}>{money(totals.total)}</dd></div>
      </dl>
      <details className="mt-3 text-sm text-neutral-600"><summary className="cursor-pointer">How this total was calculated</summary><ul className="mt-2 list-disc pl-5">{totals.steps.map((s) => <li key={s}>{s}</li>)}</ul></details>
    </div>
  );
}

function Block({ title, text }) {
  if (!text) return null;
  return <section className="mt-6"><h2 className="text-xl font-bold uppercase">{title}</h2><p className="mt-1 whitespace-pre-line text-base">{text}</p></section>;
}

export default function QuoteView() {
  useSeo("Your Estimate");
  const { token } = useParams();
  const [q, setQ] = useState(null);
  const [err, setErr] = useState("");
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formErr, setFormErr] = useState("");
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex";
    document.head.appendChild(m);
    api.get(`/q/${token}`).then((r) => setQ(r.data)).catch((e) => setErr(errMsg(e, "Estimate not found.")));
    return () => m.remove();
  }, [token]);
  const act = async (kind) => {
    if (kind === "accept" && (name.trim().length < 2 || !agree)) return setFormErr("Please type your full name and check the box to approve.");
    setBusy(true);
    setFormErr("");
    try {
      const r = await api.post(`/q/${token}/${kind}`, kind === "accept" ? { full_name: name, agree } : {});
      setQ(r.data);
    } catch (e) {
      setFormErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  if (err) return <div className="wrap py-20"><h1 className="text-4xl font-bold uppercase">Estimate unavailable</h1><p className="mt-3 text-lg" data-testid="quote-view-error">{err}</p><CallButton className="btn-red mt-6" testid="quote-view-call" /></div>;
  if (!q) return <div className="wrap py-20"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  return (
    <div className="bg-[#F5F5F5]" data-testid="quote-view-page">
      <div className="wrap max-w-4xl py-10">
        <div className="card">
          <p className="eyebrow">Written estimate · {q.ref} · Version {q.version}</p>
          <h1 className="mt-2 text-4xl font-extrabold uppercase">Hi {q.first_name}, here's your estimate</h1>
          <p className="mt-2 text-base text-neutral-700">Nothing is scheduled or charged until you approve. Any travel charge is listed as its own line below. {q.expiration_date && `Valid until ${q.expiration_date}.`}</p>
          <Block title="Scope of work" text={q.scope} />
          <Block title="Included" text={q.inclusions} />
          <Block title="Not included" text={q.exclusions} />
          <section className="mt-6"><h2 className="mb-2 text-xl font-bold uppercase">Pricing</h2><TotalsTable totals={q.totals} /></section>
          {q.discount?.apply && (
            <section className="mt-6 rounded-md border-2 border-[#D9232E] p-4" data-testid="quote-view-senior-terms">
              <h2 className="text-lg font-bold uppercase">Senior discount{q.discount.provisional ? " (provisional — eligibility not yet verified)" : ""}</h2>
              <p className="text-base">{q.discount.percent}% off {q.discount.scope === "labor" ? "eligible labor" : "eligible charges"}.{q.discount.scope !== "labor" && q.discount.excluded_categories?.length ? ` Excludes: ${q.discount.excluded_categories.map((c) => CAT[c] || c).join(", ")}.` : ""} {q.senior_terms?.lifetime_terms}</p>
            </section>
          )}
          <Block title="Payment terms" text={q.payment_terms} />
          <div className="mt-8 border-t border-neutral-200 pt-6">
            {q.status === "accepted" && <p className="flex items-center gap-2 text-lg font-bold text-green-800" data-testid="quote-view-accepted"><CheckCircle2 />Approved by {q.acceptance.full_name} on {new Date(q.acceptance.at).toLocaleString()} (version {q.acceptance.version}). We'll contact you to confirm an appointment.</p>}
            {q.status === "declined" && <p className="text-lg font-bold" data-testid="quote-view-declined">You declined this estimate. Call us anytime if you'd like a revised one.</p>}
            {q.status === "superseded" && <p className="text-lg font-bold" data-testid="quote-view-superseded">This version has been replaced by a newer estimate. Please use the latest link we sent you.</p>}
            {q.status === "sent" && q.is_expired && <p className="text-lg font-bold" data-testid="quote-view-expired">This estimate has expired. Please call us for an updated estimate.</p>}
            {q.status === "sent" && !q.is_expired && (
              <div data-testid="quote-accept-form">
                <h2 className="text-2xl font-bold uppercase">Approve this estimate</h2>
                <label htmlFor="acc-name" className="field-label mt-4">Type your full name</label>
                <input id="acc-name" className="input max-w-md" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" data-testid="accept-name-input" />
                <label className="mt-4 flex items-start gap-3 text-base"><input type="checkbox" className="mt-1 h-6 w-6 accent-[#D9232E]" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="accept-agree-check" />I approve version {q.version} of this estimate, including the scope, exclusions and total of {money(q.totals.total)}. I understand an appointment is confirmed separately.</label>
                {formErr && <p className="field-error" role="alert" data-testid="accept-error">{formErr}</p>}
                <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                  <button className="btn-red" disabled={busy} onClick={() => act("accept")} data-testid="accept-quote-btn">Approve estimate</button>
                  <button className="btn-outline" disabled={busy} onClick={() => act("decline")} data-testid="decline-quote-btn">Decline</button>
                  <CallButton className="btn-outline" label="Questions? Call" testid="quote-view-call-btn" />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
